import { FunctionComponent, memo, useMemo } from "react";
import { addHours, startOfHour, subHours } from "date-fns";
import { BoxContent } from "@edgetier/client-components";
import { Button, SpinnerUntil } from "@edgetier/components";
import { faUndo } from "@fortawesome/pro-solid-svg-icons";
import { formatUnderscoreName } from "utilities/format-underscore-name";
import { groupMetrics } from "./anomaly-metric-values-chart.utilities";
import MetricValuesChart from "components-for/anomalies/metric-values-chart";
import MetricValuesChartDateRange from "./metric-values-chart-date-range";
import useChartDrag from "hooks-for/anomalies/use-chart-drag";
import useMetricModelOutputs from "hooks-for/models/use-metric-model-outputs";
import { HOURS_AFTER_END, HOURS_BEFORE_START } from "./anomaly-metric-values-chart.constants";
import { INTERACTIONS_TABLE_PREFIX } from "constants/interactions";
import { IProps } from "./anomaly-metric-values-chart.types";
import "./anomaly-metric-values-chart.scss";

/**
 * Show charts of each metric output by a model.
 * @param props.endDateTime   Closing date in range to chart.
 * @param props.modelId       Model's output to chart.
 * @param props.startDateTime Opening date in range to chart.
 * @returns                   Multiple line charts.
 */
const AnomalyMetricValuesChart: FunctionComponent<IProps> = ({ endDateTime, modelId, startDateTime }) => {
    // Round the start and end dates to the start of the hour so that the x  axis labels will be evenly spaced out.
    const queryParameters = {
        endDateTime: startOfHour(addHours(endDateTime, HOURS_AFTER_END)),
        startDateTime: startOfHour(subHours(startDateTime, HOURS_BEFORE_START)),
    };

    const { filterDateRange, resetChartFilter, mouseEventCallbacks } = useChartDrag({
        defaultValues: {
            startDateTime: queryParameters.startDateTime,
            endDateTime: queryParameters.endDateTime,
        },
        prefixOptions: {
            prefix: INTERACTIONS_TABLE_PREFIX,
            isPrefixOptional: false,
        },
    });

    const { data: metricModelOutputs, isLoading } = useMetricModelOutputs(modelId, queryParameters);

    // Group data by metric.
    const metrics = useMemo(() => groupMetrics(metricModelOutputs || []), [metricModelOutputs]);

    return (
        <BoxContent horizontalPadding={false}>
            <SpinnerUntil data={metricModelOutputs} isReady={!isLoading}>
                <div className="anomaly-metric-values-charts">
                    {!Array.isArray(metricModelOutputs) ? (
                        "Error"
                    ) : (
                        <>
                            <div className="anomaly-metric-values-charts__filter-info">
                                <MetricValuesChartDateRange
                                    firstDate={filterDateRange[0].date}
                                    secondDate={filterDateRange[1].date}
                                />
                                <Button
                                    disabled={filterDateRange.some(({ activeLabel }) => activeLabel === null)}
                                    icon={faUndo}
                                    onClick={() => resetChartFilter()}
                                    isIconOnly
                                />
                            </div>
                            <div className="anomaly-metric-values-charts__container">
                                {metrics.map(([metric, outputs]) => (
                                    <BoxContent key={metric}>
                                        <h3>{formatUnderscoreName(metric)}</h3>
                                        <div className="anomaly-metric-values-charts__chart">
                                            <MetricValuesChart
                                                endDateTime={endDateTime}
                                                filterRange={filterDateRange}
                                                metricModelOutputs={outputs}
                                                mouseCallbacks={mouseEventCallbacks}
                                                startDateTime={startDateTime}
                                                syncId="metrics"
                                            />
                                        </div>
                                    </BoxContent>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </SpinnerUntil>
        </BoxContent>
    );
};

export default memo(AnomalyMetricValuesChart);
